import { useState, useEffect, useCallback } from 'react';
import { Search, UserX, UserCheck } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/users?page=${page}&limit=15`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setTotalPages(data.pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleBlockUser = async (userId, currentlyBlocked) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/users/${userId}/block?blocked=${!currentlyBlocked}`, {
        method: 'PUT',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to update user');

      toast.success(currentlyBlocked ? 'User unblocked' : 'User blocked');
      await fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="font-heading text-2xl md:text-3xl text-secondary">Users</h1>
        <div className="relative md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-surface/50">
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">User</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Role</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Joined</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Status</th>
                <th className="text-right py-4 px-4 font-body font-medium text-muted-foreground text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.user_id} className="border-b border-gray-50 hover:bg-surface/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="font-body font-semibold text-primary">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-body font-medium text-secondary">{user.name}</p>
                        <p className="font-body text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <Badge className={user.role === 'admin' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}>
                      {user.role || 'user'}
                    </Badge>
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-body text-sm text-muted-foreground">
                      {user.created_at ? formatDate(user.created_at) : 'N/A'}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <Badge className={user.is_blocked ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success'}>
                      {user.is_blocked ? 'Blocked' : 'Active'}
                    </Badge>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex justify-end">
                      {user.role !== 'admin' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleBlockUser(user.user_id, user.is_blocked)}
                          className={`rounded-full ${user.is_blocked ? 'text-success hover:bg-success/10' : 'text-destructive hover:bg-destructive/10'}`}
                        >
                          {user.is_blocked ? (
                            <>
                              <UserCheck className="w-4 h-4 mr-2" />
                              Unblock
                            </>
                          ) : (
                            <>
                              <UserX className="w-4 h-4 mr-2" />
                              Block
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground font-body">No users found</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-gray-100">
            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx}
                onClick={() => setPage(idx + 1)}
                className={`w-8 h-8 rounded-full font-body text-sm transition-all ${page === idx + 1
                  ? 'bg-primary text-white'
                  : 'bg-surface text-secondary hover:bg-gray-200'
                  }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
